import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { buildMonthlyStatusReportPath } from '../briefings/reportEntryLinks.js';
import {
  ErrorState,
  LoadingSkeleton,
  PageHeader,
  StatusChip,
  KpiHeroBand,
  StatusDonutMini,
  InlineScoreCell,
  GaugeCard,
  MiniBars,
  KpiBandSkeleton,
  TableSkeleton,
  BrandedEmptyState,
  StaggerReveal,
  toast,
} from '../../components/index.js';
import { ErrorZone } from '../../components/primitives/ErrorZone.jsx';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { RiskToActionDrawer } from '../actions/ActionCenterPages.jsx';
import './governance.css';
import './governance.redesign.css';

function buildProjectAiPath(projectId) {
  const next = new URLSearchParams();
  next.set('tab', 'ai');
  return `/projects/${projectId}?${next.toString()}`;
}

// ----- severity / tone helpers --------------------------------------------
// Map free-text severity/status into the executive-traffic-light tone set.
function severityTone(raw) {
  const t = String(raw || '').toLowerCase();
  if (t.includes('critical') || t.includes('blocker') || t.includes('red') || t === 'high')
    return 'brick';
  if (t.includes('medium') || t.includes('moderate') || t.includes('amber') || t.includes('warn'))
    return 'amber';
  if (t.includes('low') || t.includes('minor') || t.includes('green') || t === 'closed')
    return 'sage';
  return 'slate';
}

function countByTone(items, getSeverity) {
  const buckets = { brick: 0, amber: 0, sage: 0, slate: 0 };
  for (const item of items) {
    buckets[severityTone(getSeverity(item))] += 1;
  }
  return buckets;
}

function severitySegments(buckets) {
  return [
    { label: 'High / Critical', count: buckets.brick, tone: 'brick' },
    { label: 'Medium', count: buckets.amber, tone: 'amber' },
    { label: 'Low', count: buckets.sage, tone: 'sage' },
    { label: 'Unscored', count: buckets.slate, tone: 'slate' },
  ];
}

function RiskMatrix({ risks = [] }) {
  const cells = [
    {
      label: 'Low probability / Low impact',
      tone: 'sage',
      predicate: (risk) => risk.probability < 0.34 && risk.impact < 0.34,
    },
    {
      label: 'Low probability / High impact',
      tone: 'amber',
      predicate: (risk) => risk.probability < 0.34 && risk.impact >= 0.34,
    },
    {
      label: 'High probability / Low impact',
      tone: 'amber',
      predicate: (risk) => risk.probability >= 0.34 && risk.impact < 0.34,
    },
    {
      label: 'High probability / High impact',
      tone: 'brick',
      predicate: (risk) => risk.probability >= 0.34 && risk.impact >= 0.34,
    },
  ];
  return (
    <div className="governance-matrix">
      {cells.map((cell) => {
        const count = risks.filter(cell.predicate).length;
        return (
          <article
            key={cell.label}
            className={`governance-matrix-card gov-tone-${cell.tone}`}
            data-empty={count === 0 ? 'true' : undefined}
          >
            <div className="eyebrow">{cell.label}</div>
            <strong>{count}</strong>
          </article>
        );
      })}
    </div>
  );
}

function formatList(items = []) {
  return items.filter(Boolean).join(', ');
}

// A risk is closed when its status matches the same resolved|closed semantics
// the closeout readiness gate uses (knowledge.service.js isClosedStatus).
function isRiskResolved(risk) {
  return /resolved|closed/i.test(String(risk?.status || ''));
}

function RiskRow({ risk, onPromote, onCreateAction, onResolve, busy, rowBusy, resolving }) {
  const resolved = isRiskResolved(risk);
  const reportPath = risk.reportability?.scopeId
    ? buildMonthlyStatusReportPath({
        scopeType: risk.reportability.scopeType || 'project',
        scopeId: risk.reportability.scopeId,
        templateId: risk.reportability.templateId || 'TPL-REPORT-MONTHLY-STATUS',
      })
    : null;
  const dependencySummary = risk.relatedDependencies?.length
    ? formatList(
        risk.relatedDependencies.map(
          (dependency) => `${dependency.title || dependency.id} (${dependency.status || 'open'})`,
        ),
      )
    : 'No linked dependencies recorded.';
  const assumptionsSummary = risk.deliveryAssumptions?.length
    ? formatList(risk.deliveryAssumptions)
    : 'No tracked delivery assumptions recorded.';
  const tone = severityTone(risk.severity || risk.status);
  // Risk score is stored 0..1 in the seed data; surface it as a 0..100 band.
  const scorePct =
    typeof risk.score === 'number'
      ? risk.score <= 1
        ? Math.round(risk.score * 100)
        : Math.round(risk.score)
      : 0;
  const blockedDeps =
    risk.relatedDependencies?.filter((d) => /block/i.test(String(d.status || ''))).length || 0;

  return (
    <article className={`governance-row-card gov-row--tone gov-tone-${tone}`}>
      <div className="row spread gov-row-head" style={{ gap: 12 }}>
        <div>
          <div className="eyebrow">{risk.id}</div>
          <h4 style={{ margin: '4px 0 6px' }}>{risk.title}</h4>
        </div>
        <StatusChip status={resolved ? risk.status : risk.severity || risk.status || 'open'} />
      </div>
      <div className="gov-risk-meta">
        <span className="muted">
          Owner {risk.ownerUserId || 'unassigned'} · P {risk.probability ?? 0} / I{' '}
          {risk.impact ?? 0}
        </span>
        <div className="gov-score-inline" aria-label={`Risk score ${scorePct} of 100`}>
          <span className="muted small">score</span>
          <InlineScoreCell value={scorePct} max={100} tone={tone} />
        </div>
        {blockedDeps > 0 ? (
          <span className="chip brick sm">
            <span className="chip-dot" />
            {blockedDeps} blocked dep{blockedDeps === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      <dl className="governance-register-grid">
        <div>
          <dt>Mitigation</dt>
          <dd>{risk.mitigationPlan || 'No mitigation plan recorded.'}</dd>
        </div>
        <div>
          <dt>Dependencies</dt>
          <dd>{dependencySummary}</dd>
        </div>
        <div>
          <dt>Assumptions</dt>
          <dd>{assumptionsSummary}</dd>
        </div>
      </dl>
      <div className="row governance-row-actions" style={{ justifyContent: 'flex-end' }}>
        {reportPath ? (
          <Link className="btn ghost sm" to={reportPath}>
            Draft monthly status report
          </Link>
        ) : null}
        {risk.promotedActionId ? (
          /* AC-GOV-2.3 / W-2: a risk that already spawned an action shows the
             linked action (bidirectional source link) instead of the button. */
          <Link
            className="chip sage sm"
            to={`/actions/${risk.promotedActionId}`}
            data-testid={`risk-action-${risk.id}`}
          >
            Action → {risk.promotedActionId}
          </Link>
        ) : (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => onCreateAction(risk)}
            disabled={busy}
          >
            Create action
          </button>
        )}
        {risk.promotedIssueId ? (
          /* AC-GOV-2.2: an already-promoted risk shows its promoted state
             instead of the button — no duplicate promotion path. */
          <Link
            className="chip sage sm"
            to="/governance/issues"
            data-testid={`risk-promoted-${risk.id}`}
          >
            Promoted → {risk.promotedIssueId}
          </Link>
        ) : (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => onPromote(risk)}
            disabled={busy}
          >
            {rowBusy ? 'Promoting…' : 'Promote to issue'}
          </button>
        )}
        {onResolve ? (
          resolved ? (
            /* A resolved risk shows its closed state instead of the button;
               it no longer counts against closeout readiness. */
            <span className="chip sage sm" data-testid={`risk-resolved-${risk.id}`}>
              Resolved
            </span>
          ) : (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => onResolve(risk)}
              disabled={busy}
            >
              {resolving ? 'Resolving…' : 'Mark resolved'}
            </button>
          )
        ) : null}
      </div>
    </article>
  );
}

export function GovernanceHomePage() {
  usePageMetadata({
    title: 'Governance Command Center',
    subtitle: 'Risk, issue, change, and EVM posture for OWHA project delivery.',
  });
  const surfaces = [
    {
      to: '/governance/risks',
      eyebrow: 'Register',
      title: 'Risk Register',
      desc: 'Open risks, probability-impact posture, and promotion into tracked issues.',
      tone: 'brick',
      icon: '⚠',
    },
    {
      to: '/governance/issues',
      eyebrow: 'Resolution',
      title: 'Issue Log',
      desc: 'Operational blockers requiring owner action, escalation, and governance review.',
      tone: 'amber',
      icon: '◆',
    },
    {
      to: '/governance/changes',
      eyebrow: 'Control',
      title: 'Change Requests',
      desc: 'Schedule, cost, and staffing impact before board approval and applied execution.',
      tone: 'blue',
      icon: '⇄',
    },
    {
      to: '/governance/evm',
      eyebrow: 'PMO',
      title: 'PMO / EVM Dashboard',
      desc: 'CPI, SPI, variance formulas, and human-review-pending project health signals.',
      tone: 'sage',
      icon: '◷',
    },
  ];
  return (
    <>
      <PageHeader
        eyebrow="OWHA Governance"
        title="Governance Command Center"
        subtitle="PMO oversight across active risks, approved changes, and delivery variance."
      />
      <StaggerReveal as="section" className="governance-home-grid">
        {surfaces.map((surface) => (
          <Link
            key={surface.to}
            className={`governance-home-card gov-tone-${surface.tone}`}
            to={surface.to}
          >
            <span className="gov-home-icon" aria-hidden="true">
              {surface.icon}
            </span>
            <div className="eyebrow">{surface.eyebrow}</div>
            <h3>{surface.title}</h3>
            <p>{surface.desc}</p>
            <span className="gov-home-cue" aria-hidden="true">
              Open →
            </span>
          </Link>
        ))}
      </StaggerReveal>
    </>
  );
}

export function RiskRegisterPage() {
  usePageMetadata({
    title: 'Risk Register',
    subtitle: 'Probability, impact, and mitigation posture across OWHA projects.',
  });
  const risks = useApi('/risks/register?limit=200');
  const mutation = useMutation();
  const navigate = useNavigate();
  // SM-03: promotion confirms first, shows result feedback with a link to the
  // created issue, runs with per-row busy state, and refreshes the register.
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promotingRiskId, setPromotingRiskId] = useState(null);
  // AC-GOV-2.3 / W-2: "Create action" opens the shared risk→action drawer
  // (owner people picker + due date). The server stamps promotedActionId on
  // the risk, so after creation the row renders the linked action instead of
  // the button and duplicate attempts 409 with a deep link.
  const [actionTarget, setActionTarget] = useState(null);
  // Governed risk closure — confirm with a required rationale, PATCH the risk
  // to a resolved status, and refresh so the row shows its closed state and the
  // risk drops out of open-risk closeout gating.
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolvingRiskId, setResolvingRiskId] = useState(null);

  async function promoteRisk(risk) {
    setPromotingRiskId(risk.id);
    try {
      const response = await mutation.run(() =>
        api.post(`/risks/${risk.id}/promote-to-issue`, {
          dueDate: risk.targetResolutionDate || null,
        }),
      );
      const payload = response?.data || response;
      const issueId = payload?.issue?.id;
      toast.success({
        title: 'Risk promoted to issue',
        body: `${risk.id} → ${issueId || 'new issue'} with a linked resolution action.`,
        action: { label: 'Open issue log', onClick: () => navigate('/governance/issues') },
      });
      await risks.refresh();
    } catch (error) {
      toast.error({ title: 'Promotion failed', body: error.message });
    } finally {
      setPromotingRiskId(null);
    }
  }

  async function resolveRisk(risk, resolutionNote) {
    setResolvingRiskId(risk.id);
    try {
      await mutation.run(() =>
        api.patch(`/risks/${risk.id}`, { status: 'resolved', resolutionNote }),
      );
      toast.success({
        title: 'Risk marked resolved',
        body: `${risk.id} is resolved and cleared from the open risk register.`,
      });
      await risks.refresh();
    } catch (error) {
      toast.error({ title: 'Resolution failed', body: error.message });
    } finally {
      setResolvingRiskId(null);
    }
  }

  if (risks.loading)
    return (
      <>
        <KpiBandSkeleton count={4} />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (risks.error) return <ErrorState error={risks.error} onRetry={risks.refresh} />;

  const rows = risks.data?.data || [];
  const totalRows = risks.data?.page?.total ?? rows.length;
  const summary = risks.data?.summary || {};
  const buckets = countByTone(rows, (r) => r.severity || r.status);
  const highCount = buckets.brick;

  const kpiItems = [
    {
      id: 'open-risks',
      label: 'Open risks',
      value: summary.openRisks ?? rows.length,
      status: highCount > 0 ? 'brick' : rows.length > 0 ? 'amber' : 'sage',
      statusLabel: highCount > 0 ? `${highCount} high` : 'monitored',
      icon: '⚠',
    },
    {
      id: 'linked-deps',
      label: 'Linked dependencies',
      value: summary.linkedDependencies ?? 0,
      footer: 'Cross-project couplings tracked',
    },
    {
      id: 'blocked-deps',
      label: 'Blocked dependencies',
      value: summary.blockedDependencies ?? 0,
      status: (summary.blockedDependencies ?? 0) > 0 ? 'brick' : 'sage',
      statusLabel: (summary.blockedDependencies ?? 0) > 0 ? 'action needed' : 'clear',
      icon: '⛔',
    },
    {
      id: 'assumptions',
      label: 'Tracked assumptions',
      value: summary.assumptionCount ?? 0,
      footer: `${summary.mitigationsTracked ?? 0} mitigations tracked`,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="OWHA Governance"
        title="Risk Register"
        subtitle="Governed view of active OWHA delivery risks and mitigation readiness."
      />
      <KpiHeroBand eyebrow="Risk posture" asOf={risks.loadedAt} items={kpiItems} columns={4} />
      <section className="governance-panel gov-bento">
        <div className="gov-bento__main">
          <div className="row spread" style={{ gap: 12 }}>
            <h3>Probability × Impact Matrix</h3>
            <span className="chip outline sm">{totalRows} open records</span>
          </div>
          <RiskMatrix risks={rows} />
        </div>
        <aside className="gov-bento__side">
          <div className="eyebrow">Severity mix</div>
          {rows.length ? (
            <StatusDonutMini
              segments={severitySegments(buckets)}
              size={120}
              totalLabel="risks"
              ariaLabel="Risk severity distribution"
            />
          ) : (
            <p className="muted small">No risks to score yet.</p>
          )}
        </aside>
      </section>
      <section className="governance-panel">
        {rows.length === 0 ? (
          <BrandedEmptyState
            icon="🛡"
            eyebrow="Risk register"
            title="No risks found"
            description="Approved intake and active projects will surface risks here."
            tone="sage"
          />
        ) : (
          <StaggerReveal className="governance-stack">
            {rows.map((risk) => (
              <RiskRow
                key={risk.id}
                risk={risk}
                onPromote={(target) => setPromoteTarget(target)}
                onCreateAction={(target) => setActionTarget(target)}
                onResolve={(target) => setResolveTarget(target)}
                busy={mutation.running}
                rowBusy={promotingRiskId === risk.id}
                resolving={resolvingRiskId === risk.id}
              />
            ))}
          </StaggerReveal>
        )}
      </section>
      <RiskToActionDrawer
        open={Boolean(actionTarget)}
        onClose={() => setActionTarget(null)}
        risk={actionTarget}
        project={null}
        onCreated={() => risks.refresh()}
      />
      <ConfirmActionDialog
        open={Boolean(promoteTarget)}
        title={`Promote ${promoteTarget?.id || ''} to issue?`}
        body={`Promoting "${promoteTarget?.title || ''}" creates a tracked issue and a linked resolution action, and marks this risk as promoted. A promoted risk cannot be promoted again.`}
        confirmLabel="Promote to issue"
        busy={mutation.running}
        onCancel={() => setPromoteTarget(null)}
        onConfirm={async () => {
          const target = promoteTarget;
          setPromoteTarget(null);
          if (target) await promoteRisk(target);
        }}
      />
      <ConfirmActionDialog
        open={Boolean(resolveTarget)}
        title={`Mark ${resolveTarget?.id || ''} resolved?`}
        body={`Resolving "${resolveTarget?.title || ''}" closes it out of the open risk register and counts toward project closeout readiness. The decision and rationale are audited.`}
        confirmLabel="Mark resolved"
        requireNote
        noteLabel="Resolution rationale"
        notePlaceholder="Document how this risk was retired or why it no longer applies."
        busy={mutation.running}
        onCancel={() => setResolveTarget(null)}
        onConfirm={async (note) => {
          const target = resolveTarget;
          setResolveTarget(null);
          if (target) await resolveRisk(target, note);
        }}
      />
    </>
  );
}

export function IssueLogPage() {
  usePageMetadata({
    title: 'Issue Log',
    subtitle: 'Tracked blockers that have been promoted for operational resolution.',
  });
  const issues = useApi('/issues');

  if (issues.loading)
    return (
      <>
        <KpiBandSkeleton count={3} />
        <TableSkeleton rows={6} />
      </>
    );
  if (issues.error) return <ErrorState error={issues.error} onRetry={issues.refresh} />;

  const rows = issues.data?.data || [];
  const buckets = countByTone(rows, (r) => r.severity || r.status);
  const openCount = rows.filter(
    (r) => !/closed|resolved|done/i.test(String(r.status || '')),
  ).length;
  const promotedCount = rows.filter((r) => r.sourceRiskId).length;

  const kpiItems = [
    {
      id: 'open-issues',
      label: 'Open issues',
      value: openCount,
      status: buckets.brick > 0 ? 'brick' : openCount > 0 ? 'amber' : 'sage',
      statusLabel:
        buckets.brick > 0 ? `${buckets.brick} high` : openCount > 0 ? 'in progress' : 'clear',
      icon: '◆',
    },
    {
      id: 'promoted',
      label: 'Promoted from risk',
      value: promotedCount,
      footer: 'Carry source-risk lineage',
    },
    {
      id: 'total-tracked',
      label: 'Total tracked',
      value: rows.length,
      footer: 'All logged blockers',
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="OWHA Governance"
        title="Issue Log"
        subtitle="Operational issues promoted out of risk posture for active resolution."
      />
      {rows.length ? (
        <section className="governance-panel gov-bento">
          <div className="gov-bento__main">
            <KpiHeroBand
              eyebrow="Issue posture"
              asOf={issues.loadedAt}
              items={kpiItems}
              columns={3}
              stagger={false}
            />
          </div>
          <aside className="gov-bento__side">
            <div className="eyebrow">Severity mix</div>
            <StatusDonutMini
              segments={severitySegments(buckets)}
              size={120}
              totalLabel="issues"
              ariaLabel="Issue severity distribution"
            />
          </aside>
        </section>
      ) : null}
      <section className="governance-panel">
        {rows.length === 0 ? (
          <BrandedEmptyState
            icon="✓"
            eyebrow="Issue log"
            title="No issues found"
            description="Promoted issues and manually tracked blockers will appear here."
            tone="sage"
          />
        ) : (
          <table className="table gov-issue-table">
            <thead>
              <tr>
                <th>Issue</th>
                <th>Source risk</th>
                <th>Owner</th>
                <th>Due date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((issue) => {
                const tone = severityTone(issue.severity || issue.status);
                return (
                  <tr key={issue.id} className={`gov-issue-row gov-tone-${tone}`}>
                    <td>
                      <strong>{issue.title || issue.id}</strong>
                      <div className="muted small">{issue.id}</div>
                    </td>
                    <td>{issue.sourceRiskId || '—'}</td>
                    <td>{issue.ownerUserId || '—'}</td>
                    <td>{issue.dueDate || '—'}</td>
                    <td>
                      <StatusChip status={issue.status || issue.severity || 'open'} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

export function ChangeRequestsPage() {
  usePageMetadata({
    title: 'Change Requests',
    subtitle: 'Governance review for schedule, cost, and staffing deltas before execution.',
  });
  const changes = useApi('/change-requests');
  const mutation = useMutation();
  const { roles } = useCurrentUser();
  // SM-04 + P2 #19 (AC-GOV-4.1): board decisions confirm with a typed
  // rationale before the PATCH, support Reject alongside Approve, and give
  // success/failure feedback. Decision buttons render only for change-board
  // roles — the API enforces the same gate server-side (shared
  // rbac.CHANGE_BOARD_ROLES), so non-board users never see a button that
  // would 403.
  const canDecide = rbac.hasAnyRole(roles, rbac.CHANGE_BOARD_ROLES);
  const [pendingDecision, setPendingDecision] = useState(null); // { change, approvalState }
  const [decidingChangeId, setDecidingChangeId] = useState(null);

  async function decideChange(change, approvalState, decisionRationale) {
    setDecidingChangeId(change.id);
    try {
      const response = await mutation.run(() =>
        api.patch(`/change-requests/${change.id}`, {
          approvalState,
          decisionRationale,
        }),
      );
      toast.success({
        title: approvalState === 'approved' ? 'Change approved' : 'Change rejected',
        body: `${change.id} marked ${approvalState}.`,
      });
      const updatedChange = response?.data || response;
      if (updatedChange?.id) {
        changes.setData((current) => {
          if (!current?.data) return current;
          return {
            ...current,
            data: current.data.map((row) =>
              row.id === updatedChange.id ? { ...row, ...updatedChange } : row,
            ),
          };
        });
        return;
      }
      await changes.refresh();
    } catch (error) {
      toast.error({
        title: approvalState === 'approved' ? 'Approval failed' : 'Rejection failed',
        body: error.message,
      });
    } finally {
      setDecidingChangeId(null);
    }
  }

  if (changes.loading)
    return (
      <>
        <KpiBandSkeleton count={4} />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (changes.error) return <ErrorState error={changes.error} onRetry={changes.refresh} />;

  const rows = changes.data?.data || [];
  const isPending = (s) => /submit|pending|review/i.test(String(s || ''));
  const pendingCount = rows.filter((r) => isPending(r.approvalState)).length;
  const approvedCount = rows.filter((r) => /approv/i.test(String(r.approvalState || ''))).length;
  const totalScheduleDelta = rows.reduce(
    (a, r) => a + (Number(r.impactSummary?.scheduleDeltaDays) || 0),
    0,
  );
  const totalBudgetDelta = rows.reduce(
    (a, r) => a + (Number(r.impactSummary?.budgetDeltaUsdM) || 0),
    0,
  );

  const kpiItems = [
    {
      id: 'pending',
      label: 'Awaiting board',
      value: pendingCount,
      status: pendingCount > 0 ? 'amber' : 'sage',
      statusLabel: pendingCount > 0 ? 'decision pending' : 'clear',
      icon: '⇄',
    },
    {
      id: 'approved',
      label: 'Approved',
      value: approvedCount,
      status: 'sage',
      footer: 'Cleared for execution',
    },
    {
      id: 'sched-delta',
      label: 'Net schedule shift',
      value: totalScheduleDelta,
      unit: ' days',
      status: totalScheduleDelta > 0 ? 'amber' : 'sage',
      icon: '📅',
    },
    {
      id: 'budget-delta',
      label: 'Net budget delta',
      value: `$${Number(totalBudgetDelta.toFixed(2))}`,
      unit: 'M',
      status: totalBudgetDelta > 0 ? 'amber' : 'sage',
      icon: '$',
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="OWHA Governance"
        title="Change Requests"
        subtitle="Board-ready review of requested schedule, staffing, and budget movement."
      />
      {rows.length ? (
        <KpiHeroBand
          eyebrow="Change control"
          asOf={changes.loadedAt}
          items={kpiItems}
          columns={4}
        />
      ) : null}
      <section className="governance-panel">
        {rows.length === 0 ? (
          <BrandedEmptyState
            icon="⇄"
            eyebrow="Change control"
            title="No change requests found"
            description="Submitted changes will appear here with derived impact before approval."
            tone="slate"
          />
        ) : (
          <StaggerReveal className="governance-stack">
            {rows.map((change) => {
              const pending = isPending(change.approvalState);
              const tone = pending
                ? 'amber'
                : /approv/i.test(String(change.approvalState || ''))
                  ? 'sage'
                  : 'slate';
              const schedule = Number(change.impactSummary?.scheduleDeltaDays) || 0;
              const budget = Number(change.impactSummary?.budgetDeltaUsdM) || 0;
              const staffing = Number(change.impactSummary?.staffingDeltaHours) || 0;
              return (
                <article
                  key={change.id}
                  className={`governance-row-card gov-row--tone gov-tone-${tone}`}
                >
                  <div className="row spread gov-row-head" style={{ gap: 12 }}>
                    <div>
                      <div className="eyebrow">{change.changeType || 'change'}</div>
                      <h4 style={{ margin: '4px 0 6px' }}>{change.description || change.id}</h4>
                    </div>
                    <StatusChip status={change.approvalState || 'submitted'} />
                  </div>
                  <div className="governance-impact-grid gov-impact">
                    <div>
                      <span className="muted small">schedule delta</span>
                      <strong>{change.impactSummary?.scheduleDeltaDays ?? 0} days</strong>
                      <MiniBars
                        values={[0, schedule]}
                        tone={schedule > 0 ? 'amber' : 'sage'}
                        height={22}
                        ariaLabel="Schedule delta"
                      />
                    </div>
                    <div>
                      <span className="muted small">budget delta</span>
                      <strong>${change.impactSummary?.budgetDeltaUsdM ?? 0}M</strong>
                      <MiniBars
                        values={[0, budget]}
                        tone={budget > 0 ? 'amber' : 'sage'}
                        height={22}
                        ariaLabel="Budget delta"
                      />
                    </div>
                    <div>
                      <span className="muted small">staffing delta</span>
                      <strong>{change.impactSummary?.staffingDeltaHours ?? 0} hours</strong>
                      <MiniBars
                        values={[0, staffing]}
                        tone={staffing > 0 ? 'amber' : 'sage'}
                        height={22}
                        ariaLabel="Staffing delta"
                      />
                    </div>
                  </div>
                  {pending && canDecide ? (
                    <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => setPendingDecision({ change, approvalState: 'rejected' })}
                        disabled={mutation.running}
                      >
                        {decidingChangeId === change.id ? 'Working…' : 'Reject change'}
                      </button>
                      <button
                        type="button"
                        className="btn primary sm"
                        onClick={() => setPendingDecision({ change, approvalState: 'approved' })}
                        disabled={mutation.running}
                      >
                        {decidingChangeId === change.id ? 'Working…' : 'Approve change'}
                      </button>
                    </div>
                  ) : pending ? (
                    <p className="muted small" data-testid="change-board-gate-note">
                      Pending change-board decision — deciding requires a board role.
                    </p>
                  ) : null}
                </article>
              );
            })}
          </StaggerReveal>
        )}
      </section>
      <ConfirmActionDialog
        open={Boolean(pendingDecision)}
        title={`${pendingDecision?.approvalState === 'approved' ? 'Approve' : 'Reject'} change ${pendingDecision?.change?.id || ''}?`}
        body={
          pendingDecision?.approvalState === 'approved'
            ? 'Approving clears this change for execution and applies its schedule, budget, and staffing deltas. The decision and rationale are audited.'
            : 'Rejecting closes this change request without applying its deltas. The decision and rationale are audited.'
        }
        confirmLabel={
          pendingDecision?.approvalState === 'approved' ? 'Confirm approval' : 'Confirm rejection'
        }
        tone={pendingDecision?.approvalState === 'approved' ? 'primary' : 'danger'}
        requireNote
        noteLabel="Decision rationale"
        notePlaceholder="Document the board's basis for this change decision."
        busy={mutation.running}
        onCancel={() => setPendingDecision(null)}
        onConfirm={async (note) => {
          const decision = pendingDecision;
          setPendingDecision(null);
          if (decision) await decideChange(decision.change, decision.approvalState, note);
        }}
      />
    </>
  );
}

function formatMetric(value) {
  if (value === null || value === undefined) return '—';
  return Number.isFinite(Number(value))
    ? Number(value).toFixed(2).replace(/\.00$/, '')
    : String(value);
}

// CPI/SPI: >=1.0 on track (sage), >=0.9 watch (amber), else off-track (brick).
function indexTone(value) {
  const v = Number(value);
  if (!Number.isFinite(v)) return 'slate';
  if (v >= 1) return 'sage';
  if (v >= 0.9) return 'amber';
  return 'brick';
}

export function EvmDashboardPage() {
  usePageMetadata({
    title: 'PMO / EVM Dashboard',
    subtitle: 'Explainable variance, CPI/SPI posture, and health-check review state.',
  });
  const evm = useApi('/evm');
  const healthChecks = useApi('/project-health-checks');

  if (evm.loading || healthChecks.loading)
    return (
      <>
        <KpiBandSkeleton count={4} />
        <LoadingSkeleton rows={6} />
      </>
    );

  const evmRows = evm.data?.data || [];
  const healthRows = healthChecks.data?.data || [];
  const latestHealthByProject = new Map(healthRows.map((row) => [row.projectId, row]));

  const cpiVals = evmRows.map((r) => Number(r.cpi)).filter(Number.isFinite);
  const spiVals = evmRows.map((r) => Number(r.spi)).filter(Number.isFinite);
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const avgCpi = avg(cpiVals);
  const avgSpi = avg(spiVals);
  const atRiskCount = evmRows.filter((r) => Number(r.cpi) < 0.9 || Number(r.spi) < 0.9).length;
  const pendingReview = healthRows.filter((r) =>
    /pending|review/i.test(String(r.reviewState || '')),
  ).length;

  return (
    <>
      <PageHeader
        eyebrow="OWHA Governance"
        title="PMO / EVM Dashboard"
        subtitle="Delivery variance and human-review posture for executive-ready governance reporting."
      />
      {/* AC-STATE-1: each fetch banners its own zone; the other still renders. */}
      {healthChecks.error ? (
        <ErrorZone
          title="project health checks"
          error={healthChecks.error}
          onRetry={healthChecks.refresh}
        >
          <span className="muted small">
            EVM snapshots below still render; review-state chips fall back to no_review.
          </span>
        </ErrorZone>
      ) : null}
      {evm.error ? (
        <ErrorZone title="EVM snapshots" error={evm.error} onRetry={evm.refresh} />
      ) : null}
      {evmRows.length ? (
        <section className="gov-evm-hero">
          <div className="gov-gauge-row">
            <GaugeCard
              value={Math.round(avgCpi * 100)}
              min={0}
              max={150}
              unit="%"
              label="Avg CPI"
              sublabel={`Cost performance · ${avgCpi.toFixed(2)} index`}
              tone={indexTone(avgCpi)}
            />
            <GaugeCard
              value={Math.round(avgSpi * 100)}
              min={0}
              max={150}
              unit="%"
              label="Avg SPI"
              sublabel={`Schedule performance · ${avgSpi.toFixed(2)} index`}
              tone={indexTone(avgSpi)}
            />
          </div>
          <KpiHeroBand
            eyebrow="Portfolio EVM"
            asOf={evm.loadedAt}
            columns={2}
            items={[
              {
                id: 'snapshots',
                label: 'EVM snapshots',
                value: evmRows.length,
                footer: 'Active scope records',
              },
              {
                id: 'at-risk',
                label: 'At-risk scopes',
                value: atRiskCount,
                status: atRiskCount > 0 ? 'brick' : 'sage',
                statusLabel: atRiskCount > 0 ? 'CPI/SPI < 0.9' : 'on track',
                icon: '⚠',
              },
              {
                id: 'pending-review',
                label: 'Pending human review',
                value: pendingReview,
                status: pendingReview > 0 ? 'amber' : 'sage',
                statusLabel: pendingReview > 0 ? 'awaiting sign-off' : 'reviewed',
                icon: '◷',
              },
              {
                id: 'health-checks',
                label: 'Health checks',
                value: healthRows.length,
                footer: 'Citation-backed reviews',
              },
            ]}
          />
        </section>
      ) : null}
      <section className="governance-panel">
        {evm.error ? null : evmRows.length === 0 ? (
          <BrandedEmptyState
            icon="📈"
            eyebrow="Earned value"
            title="No EVM snapshots found"
            description="Seeded or generated EVM snapshots will appear here."
            tone="slate"
          />
        ) : (
          <StaggerReveal className="governance-stack">
            {evmRows.map((row) => {
              const healthCheck = latestHealthByProject.get(row.scopeId);
              const cpiT = indexTone(row.cpi);
              const spiT = indexTone(row.spi);
              const worstTone =
                cpiT === 'brick' || spiT === 'brick'
                  ? 'brick'
                  : cpiT === 'amber' || spiT === 'amber'
                    ? 'amber'
                    : 'sage';
              const cvNum = Number(row.cv);
              return (
                <article
                  key={row.id}
                  className={`governance-row-card gov-row--tone gov-tone-${worstTone}`}
                >
                  <div className="row spread gov-row-head" style={{ gap: 12 }}>
                    <div>
                      <div className="eyebrow">{row.id}</div>
                      <h4 style={{ margin: '4px 0 6px' }}>
                        {row.scopeId || humanizeEnum(row.scopeType, 'Scope')}
                      </h4>
                    </div>
                    <StatusChip status={healthCheck?.reviewState || 'no_review'} />
                  </div>
                  <div className="governance-impact-grid gov-evm-metrics">
                    <div>
                      <span className="muted small">CPI</span>
                      <strong className={`gov-metric gov-metric--${cpiT}`}>
                        {formatMetric(row.cpi)}
                      </strong>
                    </div>
                    <div>
                      <span className="muted small">SPI</span>
                      <strong className={`gov-metric gov-metric--${spiT}`}>
                        {formatMetric(row.spi)}
                      </strong>
                    </div>
                    <div>
                      <span className="muted small">Cost variance</span>
                      <strong
                        className={`gov-metric ${Number.isFinite(cvNum) && cvNum < 0 ? 'gov-metric--brick' : 'gov-metric--sage'}`}
                      >
                        {formatMetric(row.cv)}
                      </strong>
                    </div>
                  </div>
                  <div className="governance-formula-list">
                    <span>{row.formulas?.cpi}</span>
                    <span>{row.formulas?.spi}</span>
                  </div>
                  {healthCheck ? (
                    <p className="muted">
                      <span title={healthCheck.reviewState}>
                        {humanizeEnum(healthCheck.reviewState, 'No review')}
                      </span>{' '}
                      · <span>{healthCheck.citationIds?.length || 0} citations</span>
                    </p>
                  ) : null}
                  {String(row.scopeType || '').toLowerCase() === 'project' ||
                  String(row.scopeId || '').startsWith('PRJ-') ? (
                    <div className="row" style={{ justifyContent: 'flex-end', marginTop: 10 }}>
                      <Link className="btn ghost sm" to={buildProjectAiPath(row.scopeId)}>
                        Open project AI workspace
                      </Link>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </StaggerReveal>
        )}
      </section>
    </>
  );
}
